import React, { useState } from 'react';
import {
  Card, Button, DatePicker, Space, Alert, Descriptions, Statistic, Row, Col, Typography, message,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import moment from 'moment';
import { fiscalDataService } from '../../services/api';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const pesos = (v) =>
  `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Estimación ISR/IVA de la empresa seleccionada.
 *
 * Usa el mismo endpoint que ve el dueño: el backend resuelve el acceso por
 * actor, así que el cálculo es idéntico por construcción, no por equivalencia.
 *
 * Depende de la descarga masiva del SAT, que puede no estar lista para el rango
 * pedido. Ese estado se muestra tal cual en vez de fingir un resultado.
 */
const EstimationPanel = ({ empresa }) => {
  const [rango, setRango] = useState([moment().startOf('month'), moment().endOf('month')]);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [aviso, setAviso] = useState(null);

  const calcular = async () => {
    setCargando(true);
    setDatos(null);
    setAviso(null);

    try {
      const respuesta = await fiscalDataService.getTaxEstimation(
        empresa.id,
        rango?.[0]?.format('YYYY-MM-DD'),
        rango?.[1]?.format('YYYY-MM-DD')
      );
      setDatos(respuesta?.data || respuesta);
    } catch (err) {
      const cuerpo = err?.response?.data;

      // 428 / 409: la descarga masiva del SAT no está lista para ese rango.
      // No es un error del usuario ni un fallo de permisos.
      if (cuerpo?.sync_required) {
        setAviso({
          tipo: cuerpo.sync_state === 'in_progress' ? 'info' : 'warning',
          mensaje: cuerpo.error,
        });
      } else {
        message.error(cuerpo?.error || 'No se pudo calcular la estimación');
      }
    } finally {
      setCargando(false);
    }
  };

  const est = datos?.estimation;

  return (
    <Card
      title="Estimación de impuestos"
      extra={
        <Space>
          <RangePicker value={rango} onChange={setRango} allowClear={false} />
          <Button type="primary" icon={<CalculatorOutlined />} loading={cargando} onClick={calcular}>
            Calcular
          </Button>
        </Space>
      }
    >
      {empresa?.fiscal_regimen !== '626' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="La estimación solo aplica al régimen RESICO (626)"
          description={`Esta empresa tiene régimen ${empresa?.fiscal_regimen || 'sin definir'}.`}
        />
      )}

      {aviso && (
        <Alert
          type={aviso.tipo}
          showIcon
          style={{ marginBottom: 16 }}
          message={aviso.mensaje}
          description="Los datos provienen de la descarga masiva del SAT. Vuelve a intentar cuando esté disponible."
        />
      )}

      {!est && !aviso && (
        <Text type="secondary">Selecciona un periodo y calcula para ver la estimación.</Text>
      )}

      {est && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Statistic title="Ingresos del periodo" value={pesos(est.ingresos)} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic title="ISR a pagar" value={pesos(est.isr_a_pagar)} valueStyle={{ color: '#cf1322' }} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={est.iva_a_favor > 0 ? 'IVA a favor' : 'IVA a pagar'}
                value={pesos(est.iva_a_favor > 0 ? est.iva_a_favor : est.iva_a_pagar)}
                valueStyle={{ color: est.iva_a_favor > 0 ? '#3f8600' : '#cf1322' }}
              />
            </Col>
          </Row>

          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Tasa ISR aplicada">
              {(Number(est.tasa_isr || 0) * 100).toFixed(2)}%
            </Descriptions.Item>
            <Descriptions.Item label="ISR calculado">{pesos(est.isr_calculado)}</Descriptions.Item>
            <Descriptions.Item label="ISR retenido">{pesos(est.isr_retenido)}</Descriptions.Item>
            <Descriptions.Item label="IVA trasladado">{pesos(est.iva_trasladado)}</Descriptions.Item>
            <Descriptions.Item label="IVA retenido">{pesos(est.iva_retenido)}</Descriptions.Item>
            <Descriptions.Item label="IVA acreditable">{pesos(est.iva_acreditable)}</Descriptions.Item>
          </Descriptions>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Estimación informativa calculada sobre los CFDI descargados del SAT para el periodo.
            No sustituye la declaración.
          </Text>
        </Space>
      )}
    </Card>
  );
};

export default EstimationPanel;
